import React, { useState } from 'react'
import { Button } from '../ui/button'
import { Card } from '../ui/card'
import { MoreVertical, AlertTriangle, Plus } from 'lucide-react'
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../ui/dropdown-menu'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip'

interface Material {
  id: string
  name: string
  unit_type: string
  current_stock: number
  min_threshold: number
  cost_per_unit: number
  supplier?: string | null
}

interface MaterialCardProps {
  material: Material
  onDelete: () => void
  onEdit: () => void
  onRestock: () => void
}

function MaterialCard({ material, onDelete, onEdit, onRestock }: MaterialCardProps) {
  const isLowStock = material.current_stock <= material.min_threshold
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <Card className="p-3 md:p-4 flex flex-col gap-2 shadow-sm border border-gray-200 bg-white relative overflow-hidden">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="font-semibold text-sm md:text-base lg:text-lg flex items-center gap-1.5 md:gap-2 min-w-0 flex-1">
          <span className="truncate">{material.name}</span>
          {isLowStock && (
            <span className="px-1.5 md:px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] md:text-xs flex items-center gap-1 flex-shrink-0" title="Low stock">
              <AlertTriangle className="w-3 h-3 md:w-4 md:h-4" /> Low
            </span>
          )}
        </div>
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" aria-label="Actions" className="focus:outline-none">
              <MoreVertical className="w-5 h-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit} aria-label="Edit">
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDelete} aria-label="Delete" className="text-red-600">
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs md:text-sm text-gray-700">
        <div className="truncate">
          <span className="font-medium">Stock:</span> {material.current_stock} {material.unit_type}
        </div>
        <div className="truncate">
          <span className="font-medium">Min:</span> {material.min_threshold} {material.unit_type}
        </div>
        <div className="truncate">
          <span className="font-medium">Cost:</span> {material.cost_per_unit} / {material.unit_type}
        </div>
        {material.supplier && (
          <div className="truncate col-span-2">
            <span className="font-medium">Supplier:</span> {material.supplier}
          </div>
        )}
      </div>
      
      <div className="flex flex-col sm:flex-row gap-2 mt-2">
        <Button 
          size="sm" 
          variant="outline" 
          onClick={onRestock}
          className="flex items-center gap-1 text-xs md:text-sm"
        >
          <Plus className="w-3 h-3 md:w-4 md:h-4" />
          Restock
        </Button>
        <div className="text-[10px] md:text-xs text-gray-500 sm:ml-auto truncate">
          Value: EGP {(material.current_stock * material.cost_per_unit).toFixed(2)}
        </div>
      </div>
    </Card>
  )
}

export { MaterialCard } 